import type { ScheduleEntry } from '../../types';
import type { ScheduleUser } from '../../components/schedule/scheduleHelpers';

export type ScheduleViewMode = 'day' | 'week' | 'month' | 'team';

export const VIEW_MODES: { key: ScheduleViewMode; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'team', label: 'Team' },
];

// ── Conflict types ──
export interface Conflict {
  type: 'double_booking' | 'overtime' | 'rest_period';
  severity: 'warning' | 'error';
  description: string;
  userId: string;
  entryIds: string[];
}

export interface CoverageGap {
  date: string;
  startHour: number;
  endHour: number;
  description: string;
}

// ── Conflict detection ──
export function detectConflicts(entries: ScheduleEntry[], users: ScheduleUser[], otThreshold: number): Conflict[] {
  const conflicts: Conflict[] = [];
  const active = entries.filter(e => e.status !== 'cancelled' && !e.all_day);

  // Group by user
  const byUser = new Map<string, ScheduleEntry[]>();
  for (const e of active) {
    for (const uid of e.assigned_to ?? []) {
      const arr = byUser.get(uid) ?? [];
      arr.push(e);
      byUser.set(uid, arr);
    }
  }

  for (const [uid, userEntries] of byUser) {
    const sorted = [...userEntries].sort((a, b) => a.start_at.localeCompare(b.start_at));
    const userName = users.find(u => u.id === uid);
    const name = userName ? `${userName.first_name} ${userName.last_name}` : 'Unknown';

    // 1. Double-booking check
    for (let i = 0; i < sorted.length - 1; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        if (sorted[j].start_at < sorted[i].end_at) {
          conflicts.push({ type: 'double_booking', severity: 'error', description: `${name} has overlapping entries`, userId: uid, entryIds: [sorted[i].id, sorted[j].id] });
        }
      }
    }

    // 2. Overtime check (total hours this week)
    const totalMinutes = sorted.reduce((sum, e) => {
      const start = new Date(e.start_at).getTime();
      const end = new Date(e.end_at).getTime();
      return sum + (end - start) / 60000;
    }, 0);
    const totalHours = totalMinutes / 60;
    if (totalHours > otThreshold) {
      conflicts.push({ type: 'overtime', severity: 'warning', description: `${name} at ${Math.round(totalHours)}/${otThreshold}h this period`, userId: uid, entryIds: sorted.map(e => e.id) });
    }

    // 3. Rest period check (< 11h between entries)
    for (let i = 0; i < sorted.length - 1; i++) {
      const endTime = new Date(sorted[i].end_at).getTime();
      const nextStart = new Date(sorted[i + 1].start_at).getTime();
      const restHours = (nextStart - endTime) / 3600000;
      if (restHours > 0 && restHours < 11) {
        conflicts.push({ type: 'rest_period', severity: 'warning', description: `${name} has ${Math.round(restHours)}h rest between entries (min 11h)`, userId: uid, entryIds: [sorted[i].id, sorted[i + 1].id] });
      }
    }
  }
  return conflicts;
}

// ── Unassigned entries ──
export function getUnassignedEntries(entries: ScheduleEntry[]): ScheduleEntry[] {
  return entries.filter(e => e.status !== 'cancelled' && (!e.assigned_to || e.assigned_to.length === 0));
}
